import { isEqual } from "lodash";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { object, string, ref, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { passwordSchemaTest } from "../../../../utils/password-utils";
import { useEffect, useRef, useState } from "react";
import { changeUser } from "../redux/usersSagas";

const USER_TYPE = {
  OWNER: "owner",
  CORPORATE: "corporate",
  MULTI_ORGANIZATION_OWNER: "multi_organization_owner",
  MULTI_ORGANIZATION_CORPORATE: "multi_organization_corporate"
};

const CORPORATE_USER_TYPES = [USER_TYPE.CORPORATE, USER_TYPE.MULTI_ORGANIZATION_CORPORATE];
const MULTI_ORGANIZATION_USER_TYPES = [
  USER_TYPE.MULTI_ORGANIZATION_OWNER,
  USER_TYPE.MULTI_ORGANIZATION_CORPORATE
];

const deriveUserType = (isCorporate, isMultiOrganizations) => {
  if (isCorporate && isMultiOrganizations) return USER_TYPE.MULTI_ORGANIZATION_CORPORATE;
  if (isMultiOrganizations) return USER_TYPE.MULTI_ORGANIZATION_OWNER;
  if (isCorporate) return USER_TYPE.CORPORATE;
  return USER_TYPE.OWNER;
};

export const schema = (isAdmin) => {
  return object({
    first_name: string().label("First Name").trim().min(2).required(),
    last_name: string().label("Last Name").trim().min(2).required(),
    email: string()
      .label("Email")
      .trim()
      .when({
        is: (value) => {
          return !!value;
        },
        then: (schema) => {
          return schema.email().required();
        }
      }),
    password: string()
      .label("Password")
      .when({ is: (value) => !!value, then: (schema) => schema.min(8).test(passwordSchemaTest) }),
    password_confirmation: string()
      .label("Password Confirmation")
      .when("password", {
        is: (password) => !!password,
        then: (schema) => schema.required().oneOf([ref("password")], "${path} must match Password")
      }),

    ...(!isAdmin
      ? {
          role_id: object()
            .required("Please choose role")
            .test({
              name: "validRole",
              test: (value, context) => {
                let { user_type, organization_id, organization_ids } = context.parent;

                if (MULTI_ORGANIZATION_USER_TYPES.includes(user_type)) {
                  return organization_ids.some((org) => org.id === value.organizationId);
                } else {
                  return organization_id.id === value.organizationId;
                }
              },
              message: "Please select role from user's organization"
            })
        }
      : {}),
    organization_ids: array().when("user_type", {
      is: (v) => MULTI_ORGANIZATION_USER_TYPES.includes(v),
      then: (schema) => {
        return schema.required().min(1, "Please Select at least 1 Organization");
      },
      otherwise: (schema) => {
        return schema.notRequired();
      }
    })
  }).required();
};

const areSameOrganizations = (current = [], defaults = []) =>
  isEqual(current.map((org) => org?.id).sort(), defaults.map((org) => org?.id).sort());

export const useEdit = ({ isAdmin, id, orgName }) => {
  const dispatch = useDispatch();

  const [orgRoles, setOrgRoles] = useState([]);

  const currentUserInfo = useSelector((state) => state.users.currentUserInfo);
  const { filters: { roles, locations, organizations } = {}, permissions } = useSelector(
    (state) => state.users.commonSettings
  );

  const {
    firstName,
    lastName,
    email,
    roleId,
    phone,
    expiredAt,
    inactive,
    isCorporate,
    isMultiOrganizations,
    passwordChanged,
    tosAccepted,
    userOrganizationIds = [],
    corporateReporting,
    linkUsersToSharePermissions = [],
    enabled2fa,
    disabled2faByEmail,
    daysInterval2fa,
    organizationId,
    permissionIds = [],
    clubreadyStoreIds = [],
    corporateReportIds = [],
    sendInactiveUsersEmail,
    canAssignRole,
    availableForAssignRoleIds = []
  } = currentUserInfo || {};

  const selectedRole = roles.find((role) => role.id == roleId);
  const availableRoles = availableForAssignRoleIds.length
    ? roles.filter((role) => availableForAssignRoleIds.includes(role.id))
    : [];

  const defaultValues = {
    first_name: firstName,
    last_name: lastName,
    email,
    role_id: selectedRole ?? null,
    phone_number: phone,
    expires_at: expiredAt,
    inactive,
    password_changed: passwordChanged,
    accepted_tos_at: tosAccepted,

    enabled_2fa: enabled2fa,
    disabled_2fa_by_email: disabled2faByEmail,
    days_interval_2fa: daysInterval2fa,
    permission_ids: permissionIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    clubready_store_ids: clubreadyStoreIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    corporate_report_ids: corporateReportIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    send_inactive_users_email: sendInactiveUsersEmail,
    password: "",
    password_confirmation: "",
    user_type: deriveUserType(isCorporate, isMultiOrganizations),
    organization_id: organizations.find((el) => el.id == organizationId) ?? null,
    organization_ids: userOrganizationIds.map((userOrgId) =>
      organizations.find((org) => org.id == userOrgId)
    ),
    can_assign_role: canAssignRole,
    available_for_assign_role_ids: availableRoles,
    corporate_reporting: corporateReporting,
    user_group_user_ids: linkUsersToSharePermissions.filter((user) => user.selected)
  };

  const form = useForm({
    resolver: yupResolver(schema(isAdmin)),
    defaultValues: {},
    values: defaultValues
  });

  let {
    unregister,
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields }
  } = form;

  const watchedValues = watch([
    "user_type",
    "corporate_reporting",
    "organization_id",
    "organization_ids",
    "can_assign_role",
    "role_id"
  ]);

  let [
    currentUserType,
    isCorporateReporting,
    currentOrg,
    currentOrgs,
    canAssignRoleWatched,
    currentRole
  ] = watchedValues;

  const isCorp = CORPORATE_USER_TYPES.includes(currentUserType);
  const isMultiOrg = MULTI_ORGANIZATION_USER_TYPES.includes(currentUserType);

  const currentOrgId = currentOrg?.id;
  // The store list is shown for every user type, but only Owner / Multi Organizations
  // Owner (i.e. non-corporate) may edit it. Corporate types see it read-only.
  const canEditLocations = !isCorp;
  const canSendInactiveUsersEmail = isMultiOrg || isCorp;

  //it is used to block permission tab when role is changed as permissions differ by role
  const canChangePermissions = currentRole?.id === selectedRole?.id;

  const selectedOrgIds = isMultiOrg
    ? (currentOrgs || []).map((org) => org.id)
    : currentOrgId != null
    ? [currentOrgId]
    : [];

  const allPermissions = {
    location_permissions: locations.filter((location) =>
      selectedOrgIds.includes(location.organizationId)
    ),
    ...permissions
  };

  const setLocations = () => {
    if (canEditLocations) {
      if (isMultiOrg) {
        // For multi-org users, organization_id is unregistered so currentOrgId is
        // always undefined. Compare the current org selection to the saved default
        // instead — preserve the hand-picked store list when orgs are unchanged,
        // reset when orgs are added or removed.
        setValue(
          "clubready_store_ids",
          areSameOrganizations(currentOrgs, defaultValues.organization_ids)
            ? defaultValues.clubready_store_ids
            : {}
        );
      } else {
        setValue(
          "clubready_store_ids",
          organizationId === currentOrgId ? defaultValues.clubready_store_ids : {}
        );
      }
    }
  };

  const setAssignRoles = () => {
    if (!isMultiOrg && canAssignRoleWatched) {
      setValue(
        "available_for_assign_role_ids",
        currentOrgId == organizationId ? defaultValues.available_for_assign_role_ids : []
      );
    }
  };

  const unregisterFields = () => {
    if (!isMultiOrg) {
      unregister("organization_ids", { keepDefaultValue: true });
    }

    if (isMultiOrg) {
      unregister("organization_id", { keepDefaultValue: true });
    }

    if (isMultiOrg || !canAssignRoleWatched) {
      unregister("available_for_assign_role_ids", { keepDefaultValue: true });
    }

    if (isMultiOrg || isCorp || !isCorporateReporting) {
      unregister("user_group_user_ids", { keepDefaultValue: true });
    }

    if (isCorp) {
      unregister("clubready_store_ids", { keepDefaultValue: true });
    }

    if (!isCorporateReporting) {
      unregister("corporate_report_ids", { keepDefaultValue: true });
    }

    if (!isMultiOrg && !isCorp) {
      unregister("send_inactive_users_email", { keepDefaultValue: true });
    }
  };

  const filterRolesList = () => {
    if (isMultiOrg && currentOrgs) {
      let orgs = currentOrgs.map((org) => org.id);
      setOrgRoles(roles.filter((role) => orgs.includes(role.organizationId)));

      return;
    }

    let orgRoles = roles.filter((role) => role.organizationId === currentOrgId);
    setOrgRoles(orgRoles);
  };

  const readyToGo = useRef(null);

  useEffect(() => {
    if (
      !readyToGo.current &&
      currentUserInfo &&
      watchedValues.every((value) => value !== undefined)
    ) {
      readyToGo.current = true;
    }
  }, [currentUserInfo, watchedValues]);

  useEffect(() => {
    if (!isAdmin && currentUserInfo && readyToGo.current) {
      unregisterFields();
      filterRolesList();
      setAssignRoles();
      setLocations();
    }
  }, [
    currentUserInfo,
    isMultiOrg,
    canAssignRoleWatched,
    isCorp,
    isCorporateReporting,
    currentOrgs,
    currentOrgId
  ]);

  const onSubmit = (data) => {
    let changedFieldKeys = Object.keys(dirtyFields);
    let formattedDataForRequest = {};

    changedFieldKeys.forEach((key) => {
      if (
        key === "clubready_store_ids" ||
        key == "permission_ids" ||
        key == "corporate_report_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: Object.keys(data[key]).map((id) => parseInt(id))
        };
      } else if (key === "role_id" || key === "organization_id") {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].id.toString()
        };
      } else if (
        key === "organization_ids" ||
        key === "available_for_assign_role_ids" ||
        key === "user_group_user_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].map((el) => el.id)
        };
      } else {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: key === "phone_number" ? data[key]?.trim() : data[key]
        };
      }
    });

    dispatch(changeUser({ data: formattedDataForRequest, id, orgName, organizationId }));
  };

  if (!currentUserInfo) return {};

  return {
    form,
    submit: handleSubmit(onSubmit),
    linkUsersToSharePermissions,
    canChangePermissions,
    currentUserInfo: {
      ...currentUserInfo,
      userActions: {
        ...currentUserInfo.userActions,
        canEditLocations,
        canSendInactiveUsersEmail,
        canUpdateCorporateReports: isCorporateReporting
      }
    },
    watchedValues: {
      isMultiOrg,
      canAssignRole: canAssignRoleWatched,
      isCorporate: isCorp,
      isCorporateReporting,
      userType: currentUserType
    },
    orgRoles,
    allPermissions,
    organizations
  };
};
